import type { AccountProfile, ChargeDraft, ChargeStep, ChinaPaymentType } from '@mypoz/shared';

/** The three steps of a request, in order, as on the old website (MYP-109). */
export const CHARGE_STEPS: readonly ChargeStep[] = ['amount', 'details', 'payment'];

/** What the customer types or picks in steps 2 and 3 of a request (MYP-109). */
export interface ChargeFields {
  readonly name: string;
  readonly phone: string;
  readonly idNumber: string;
  readonly payerAccount: string;
  readonly sellerName: string;
  readonly recipientName: string;
  readonly recipientCardNumber: string;
  readonly recipientBank: string;
  readonly recipientBranch: string;
  readonly remark: string;
  /** The MyPoz account paid into (`getBankList` id). */
  readonly bankId?: string;
}

/**
 * The fields that belong to one request type, as a fresh request of that type has them. Only proxy
 * pay's account is known in advance: the Taobao account linked to the profile.
 */
export function typeFields(type: ChinaPaymentType, profile: AccountProfile) {
  return {
    payerAccount: type === 'ALIPAY' ? (profile.taobaoAccount ?? '') : '',
    sellerName: '',
    recipientName: '',
    recipientCardNumber: '',
    recipientBank: '',
    recipientBranch: '',
  };
}

/** A fresh request: the customer's name, phone and ID number from the account, as the old site. */
export function initialChargeFields(type: ChinaPaymentType, profile: AccountProfile): ChargeFields {
  return {
    name: profile.name ?? '',
    phone: profile.phone ?? '',
    idNumber: profile.nric ?? '',
    ...typeFields(type, profile),
    remark: '',
  };
}

/** The fields, the amount and its quote, and the receipt → the draft `@mypoz/shared` checks. */
export function toChargeDraft(
  type: ChinaPaymentType,
  fields: ChargeFields,
  amount: { readonly rmbCents?: number; readonly totalRmCents?: number },
  receiptPath: string | undefined,
): ChargeDraft {
  return {
    type,
    ...(amount.rmbCents !== undefined ? { rmbCents: amount.rmbCents } : {}),
    ...(amount.totalRmCents !== undefined ? { totalRmCents: amount.totalRmCents } : {}),
    ...(fields.bankId ? { bankId: fields.bankId } : {}),
    ...(receiptPath ? { receiptPath } : {}),
    name: fields.name,
    phone: fields.phone,
    idNumber: fields.idNumber,
    payerAccount: fields.payerAccount,
    sellerName: fields.sellerName,
    recipientName: fields.recipientName,
    recipientCardNumber: fields.recipientCardNumber,
    recipientBank: fields.recipientBank,
    recipientBranch: fields.recipientBranch,
    remark: fields.remark,
  };
}
